import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "@/components/provider/BreadcrumbsProvider";
import {
  useGetCallStatsQuery,
  useGetChecklistQuery,
  useGetDashboardSummaryQuery,
  useGetHomeQuery,
} from "@/store/rest_api";
import OnboardingChecklist from "./checklist/OnboardingChecklist";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Phone, UserPlus, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import NeedsAttentionCard from "./NeedsAttentionCard";
import RecentActivityCard from "./RecentActivityCard";
import TodaysTasksCard from "./TodaysTasksCard";
import PipelineCard from "./PipelineCard";
import CampaignsCard from "./CampaignsCard";
import { A2pRegistrationBanner } from "./A2pRegistrationBanner";

type RangeKey = "today" | "7d" | "30d";

const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const QUICK_ACTIONS = [
  { label: "Power Dialer", to: "/power-dialer", icon: Phone },
  { label: "Leads", to: "/leads", icon: Users },
  { label: "Contacts", to: "/contacts", icon: UserPlus },
  { label: "Inbox", to: "/inbox", icon: MessageSquare },
];

const rangeStartIso = (range: RangeKey): string => {
  const now = new Date();
  if (range === "today") {
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.toISOString();
  }
  const days = range === "7d" ? 7 : 30;
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
};

const greetingForHour = (hour: number): string => {
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 18) {
    return "Good afternoon";
  }
  return "Good evening";
};

const StatTile = ({
  label,
  value,
  sub,
  valueClassName,
}: {
  label: string;
  value: number;
  sub?: string;
  valueClassName?: string;
}) => (
  <div className="rounded-xl border bg-card p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={cn("mt-2 text-3xl font-semibold tabular-nums", valueClassName)}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground tabular-nums">{sub}</p>}
  </div>
);

const DashboardHome = () => {
  const navigate = useNavigate();
  const { data: user } = useGetHomeQuery();
  const [range, setRange] = useState<RangeKey>("today");
  const rangeParams = useMemo(() => ({ from: rangeStartIso(range) }), [range]);
  const { data: summary } = useGetDashboardSummaryQuery(rangeParams);
  const { data: callStats } = useGetCallStatsQuery(rangeParams);

  const greeting = greetingForHour(new Date().getHours());
  const connectedPercent =
    callStats && callStats.calls > 0 ? Math.round(((callStats.calls - callStats.missed) / callStats.calls) * 100) : null;

  return (
    <div className="flex flex-col gap-6">
      <A2pRegistrationBanner />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight">
          {greeting}
          {user?.firstName ? `, ${user.firstName}` : ""}
        </h1>
        <Select value={range} onValueChange={(value) => setRange(value as RangeKey)}>
          <SelectTrigger className="h-8 w-auto gap-1.5 rounded-full px-3 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <StatTile label="New leads" value={summary?.newLeads ?? 0} />
        <StatTile
          label="Calls"
          value={callStats?.calls ?? 0}
          sub={connectedPercent !== null ? `${connectedPercent}% connected` : undefined}
        />
        <StatTile label="Conversations" value={callStats?.conversations ?? 0} valueClassName="text-success" />
        <StatTile label="Messages" value={summary?.messages ?? 0} />
        <StatTile label="Tasks due" value={summary?.tasksDue ?? 0} sub="today or overdue" />
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <NeedsAttentionCard needsAttention={summary?.needsAttention} callStats={callStats} from={rangeParams.from} />
          <RecentActivityCard />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {QUICK_ACTIONS.map((action) => (
              <Button
                key={action.to}
                variant="outline"
                className="flex h-auto flex-col gap-2 py-4"
                onClick={() => navigate(action.to)}
              >
                <action.icon className="h-5 w-5" />
                <span>{action.label}</span>
              </Button>
            ))}
          </div>
        </div>
        <aside className="flex flex-col gap-6 lg:w-[290px] lg:shrink-0 lg:border-l lg:pl-6">
          <TodaysTasksCard needsAttention={summary?.needsAttention} tasksDue={summary?.tasksDue ?? 0} />
          <PipelineCard
            stages={summary?.pipelineByStage ?? []}
            pipelineValue={summary?.pipelineValue ?? 0}
            openCount={summary?.pipelineOpenCount ?? 0}
          />
          <CampaignsCard campaigns={summary?.activeCampaigns ?? []} />
        </aside>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { setSection } = useBreadcrumbs();
  const { data: checklist } = useGetChecklistQuery();

  const requestPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (err) {
      console.error("Error requesting permissions:", err);
    }
  };

  useEffect(() => {
    setSection("Dashboard");
    requestPermissions();
  }, []);

  const showChecklist = checklist != null && checklist.progress < 100;

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      {showChecklist ? <OnboardingChecklist /> : <DashboardHome />}
    </div>
  );
};

export default Dashboard;
